import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  Zap,
  Target,
  Rocket,
  Crown,
  CheckCircle2,
  X,
  Star,
  Sparkles,
  ArrowRight,
  RefreshCw,
  Infinity,
  Gift,
  TrendingUp,
  Award,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { UserModelData, PAGE_NAME } from "../../../types";
import { actions } from "../../redux";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import LoadingIndicator from "../../components/LoadingIndicator";

const ProPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getTranslationText = useTranslationHook();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<'monthly' | 'lifetime' | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successType, setSuccessType] = useState<'monthly' | 'lifetime'>('monthly');

  const MONTHLY_PRICE = 500; // 500 Pi
  const LIFETIME_PRICE = 5000; // 5000 Pi - EXCLUSIVE OFFER

  const handleSubscribe = async (type: 'monthly' | 'lifetime') => {
    if (isLoading) return;
    
    const price = type === 'lifetime' ? LIFETIME_PRICE : MONTHLY_PRICE;
    
    if ((user?.picoinBalance || 0) < price) {
      alert(`Insufficient Pi balance. You need ${price} Pi to ${type === 'lifetime' ? 'unlock Lifetime Pro' : 'subscribe'}!`);
      return;
    }

    setIsLoading(true);
    setLoadingType(type);
    try {
      if (type === 'lifetime') {
        await arthereumProvider.activateLifetimePro(user!.id, LIFETIME_PRICE);
      } else {
        await arthereumProvider.activatePro(user!.id, MONTHLY_PRICE);
      }
      
      // Refresh user data
      const updatedUser = await arthereumProvider.getUserByUid(user!.id);
      dispatch(actions.setActualUser({ user: updatedUser, miningInfos: userData.miningInfos }));
      
      setSuccessType(type);
      setShowSuccess(true);
    } catch (error: any) {
      alert(error.message || "Subscription failed. Please try again.");
    } finally {
      setIsLoading(false);
      setLoadingType(null);
    }
  };

  const benefits = [
    {
      icon: <Zap className="text-amber-400" size={24} />,
      title: "2x Grant Multiplier",
      desc: "Permanent double grants on every single claim.",
    },
    {
      icon: <ShieldCheck className="text-emerald-400" size={24} />,
      title: "Ad-Free Experience",
      desc: "Zero intrusive ads. Claim grants instantly.",
    },
    {
      icon: <Target className="text-blue-400" size={24} />,
      title: "Priority Access",
      desc: "Higher limits and exclusive early feature access.",
    },
    {
      icon: <Crown className="text-purple-400" size={24} />,
      title: "Elite Status",
      desc: "Exclusive gold badge on your profile and contest ranking.",
    },
  ];

  const isPro = user?.isPro && new Date(user.proExpiry.seconds * 1000) > new Date();
  const isLifetimePro = user?.isLifetimePro;

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden relative">
      <CommonPageTitle title="Arthereum Pro" padding={null} />
      
      {/* Animated background elements */}
      <div className="absolute top-20 right-[-10%] w-64 h-64 bg-purple-600/20 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-40 left-[-10%] w-80 h-80 bg-blue-600/20 rounded-full blur-[120px]"></div>
      <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[150px]"></div>

      <div className="max-w-md mx-auto px-6 py-12 relative z-10 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-block p-3 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/20 mb-2"
          >
            <Crown size={40} className="text-white" />
          </motion.div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-white via-amber-200 to-white bg-clip-text text-transparent">
            Arthereum Pro
          </h1>
          <p className="text-slate-400 text-lg">
            Unlock the true potential of your Arthereum wallet.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 gap-4">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 flex items-start gap-4 hover:bg-white/10 transition-all group"
            >
              <div className="p-3 rounded-xl bg-slate-800 group-hover:scale-110 transition-transform">
                {benefit.icon}
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-lg text-white">{benefit.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{benefit.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ==================== LIFETIME PRO OFFER ==================== */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative"
        >
          {/* BEST VALUE Banner */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-6 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-500/30 flex items-center gap-2">
              <Sparkles size={14} className="animate-pulse" />
              BEST VALUE - SAVE 10X
              <Sparkles size={14} className="animate-pulse" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-red-600 rounded-3xl p-1 shadow-2xl shadow-orange-500/30">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-[22px] p-8 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 p-4 rotate-12 opacity-20">
                <Infinity size={100} className="text-amber-400" />
              </div>
              <div className="absolute bottom-0 left-0 p-4 -rotate-12 opacity-10">
                <Crown size={80} className="text-amber-400" />
              </div>

              <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                <div className="flex items-center gap-2">
                  <Award className="text-amber-400" size={28} />
                  <span className="text-amber-400 uppercase tracking-widest text-sm font-black">
                    Lifetime Pro Access
                  </span>
                  <Award className="text-amber-400" size={28} />
                </div>

                <div className="space-y-2">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-6xl font-black bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                      {LIFETIME_PRICE.toLocaleString()}
                    </span>
                    <span className="text-3xl font-bold text-amber-400">π</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-slate-400">
                    <span className="line-through text-lg">50,000 Pi</span>
                    <span className="bg-emerald-500 text-white text-xs font-black px-2 py-0.5 rounded-full">
                      90% OFF
                    </span>
                  </div>
                  <p className="text-amber-200 text-sm font-medium">
                    One-time payment. Forever Pro.
                  </p>
                </div>

                <div className="w-full space-y-3 text-left">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    <span className="text-slate-300">Never pay again - <strong className="text-white">FOREVER</strong></span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    <span className="text-slate-300">All future Pro features included</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    <span className="text-slate-300">Exclusive <strong className="text-amber-400">Founding Member</strong> badge</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    <span className="text-slate-300">Priority support & early access</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSubscribe('lifetime')}
                  disabled={isLoading || isLifetimePro}
                  className={`
                    w-full py-4 rounded-2xl font-black text-lg transition-all transform active:scale-95 flex items-center justify-center gap-2
                    ${isLifetimePro 
                      ? 'bg-emerald-500 text-white cursor-default' 
                      : 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white hover:shadow-xl hover:shadow-orange-500/30 hover:scale-[1.02]'
                    }
                  `}
                >
                  {loadingType === 'lifetime' ? (
                    <RefreshCw className="animate-spin" />
                  ) : isLifetimePro ? (
                    <>
                      <Crown />
                      <span>Lifetime Pro Active</span>
                    </>
                  ) : (
                    <>
                      <Infinity size={24} />
                      <span>Get Lifetime Pro Now</span>
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                <p className="text-amber-200/70 text-xs italic">
                  ⏰ Limited time offer. Price may increase.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ==================== MONTHLY PRO OPTION ==================== */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-8 shadow-2xl shadow-indigo-500/20 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-4 rotate-12 opacity-10">
            <Sparkles size={120} />
          </div>

          <div className="relative z-10 flex flex-col items-center text-center space-y-6">
            <div className="space-y-2">
               <span className="text-indigo-200 uppercase tracking-widest text-xs font-bold">Monthly Access</span>
               <div className="flex items-baseline justify-center gap-1">
                 <span className="text-5xl font-black">{MONTHLY_PRICE}</span>
                 <span className="text-2xl font-bold">π</span>
                 <span className="text-indigo-200 ml-1">/ month</span>
               </div>
            </div>

            <button
              onClick={() => handleSubscribe('monthly')}
              disabled={isLoading || isPro || isLifetimePro}
              className={`
                w-full py-4 rounded-2xl font-black text-lg transition-all transform active:scale-95 flex items-center justify-center gap-2
                ${(isPro || isLifetimePro)
                  ? 'bg-emerald-500 text-white cursor-default' 
                  : 'bg-white text-indigo-700 hover:bg-slate-100 hover:shadow-xl'
                }
              `}
            >
              {loadingType === 'monthly' ? (
                <RefreshCw className="animate-spin" />
              ) : (isPro || isLifetimePro) ? (
                <>
                  <CheckCircle2 />
                  <span>Subscribed</span>
                </>
              ) : (
                <>
                  <span>Unlock Monthly Pro</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <p className="text-indigo-200 text-xs">
              Cancel anytime. Rewards apply immediately.
            </p>
          </div>
        </motion.div>

        {/* Comparison hint */}
        <div className="text-center text-slate-500 text-sm">
          <p>💡 Lifetime = 10 months of Monthly Pro</p>
          <p className="text-xs mt-1">After 10 months, you're saving every single month!</p>
        </div>

        {/* Success Modal */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/80 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className={`w-full max-w-sm rounded-[40px] p-8 text-center space-y-6 shadow-2xl ${
                  successType === 'lifetime' 
                    ? 'bg-gradient-to-br from-amber-600 to-orange-600' 
                    : 'bg-indigo-600'
                }`}
              >
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-lg ${
                  successType === 'lifetime' ? 'bg-amber-400 shadow-amber-500/20' : 'bg-emerald-500 shadow-emerald-500/20'
                }`}>
                  {successType === 'lifetime' ? (
                    <Crown size={40} className="text-white" />
                  ) : (
                    <CheckCircle2 size={40} className="text-white" />
                  )}
                </div>
                <div className="space-y-2">
                  <h2 className="text-3xl font-black">
                    {successType === 'lifetime' ? 'Welcome to the Elite!' : 'Welcome to Pro!'}
                  </h2>
                  <p className="text-white/80 italic">
                    {successType === 'lifetime' 
                      ? '"You just joined the exclusive Founding Members circle."' 
                      : '"Welcome to the ELITE circle."'
                    }
                  </p>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">
                  {successType === 'lifetime' 
                    ? 'Your LIFETIME Pro membership is now active. You will never have to pay again!' 
                    : 'Your Arthereum Pro subscription is now active. All multipliers are working and ads are gone!'
                  }
                </p>
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    navigate(PAGE_NAME.HOME);
                  }}
                  className="w-full py-4 bg-white text-slate-900 rounded-2xl font-bold hover:bg-slate-100"
                >
                  {successType === 'lifetime' ? 'Start Your Lifetime Journey' : 'Start Pro Journey'}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProPage;
